import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import moment from 'moment';

import {Button} from '@dized/ui';

import GameActions from '../../actions/GameActions';
import ProjectActions from '../../actions/ProjectActions';
import TranslationActions from '../../actions/TranslationActions';
import {assetToAO1} from '../../common/utils/assetUtils';
import {getStringFromTO} from '../../common/utils/i18nUtils';
import {
  ProjectTypes,
  ProjectTypeNames,
  editProject,
  editProjectTranslations,
  reviewTutorial,
} from '../../common/utils/projectUtils';
import AssetSelector from '../../components/AssetSelector';
import {DefaultImage, Image} from '../../components/GameDetails.js';
import GameHeader from '../../components/GameHeader.js';
import {LocaleTable} from '../../components/LocaleTable';
import TranslationDownload from '../../components/TranslationDownload.js';
import ProjectDetails from '../../components/ProjectDetails.js';
import {GridButton} from '../../components/DataGrid.js';
import {Row, Title, ContentContainer} from '../../components/StyledComponents.js';
import styled from 'styled-components';

const ProjectButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  min-width: 200px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  justify-content: flex-end;

`;

const assetAttr = {
  mimeType: 'image/png, image/jpeg',
  locale: 'en-US',
  size: '[2048,2048]',
  min: true,
  max: true,
};

const projectImageContext = {
  application: 'org-portal',
  description: 'project image',
};

const mapStateToProps = (store) => ({
  organization: store.db.organization,
  projects: store.db.projects,
  games: store.db.games,
});

const mapDispatchToProps = (dispatch) => ({
  ...GameActions.bindActions(dispatch),
  ...ProjectActions.bindActions(dispatch),
  ...TranslationActions.bindActions(dispatch),
});

class ProjectInfo extends Component {
  static propTypes = {
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        gameId: PropTypes.string.isRequired,
        projectId: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    organization: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    }).isRequired,
    projects: PropTypes.shape({
      projects: PropTypes.arrayOf(
        PropTypes.shape({
          description: PropTypes.shape({
            text: PropTypes.string.isRequired,
          }).isRequired,
          projectImage: PropTypes.shape({
            assetId: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
          }),
          status: PropTypes.string.isRequired,
          title: PropTypes.shape({
            text: PropTypes.string.isRequired,
          }).isRequired,
          type: PropTypes.string.isRequired,
          uuid: PropTypes.string.isRequired,
        }),
      ),
    }).isRequired,
    games: PropTypes.shape({
      gameLookup: PropTypes.object,
      games: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.shape({
            text: PropTypes.string.isRequired,
          }).isRequired,
          uuid: PropTypes.string.isRequired,
        }),
      ),
    }),
    DELETE_PROJECT_LOCALE: PropTypes.func.isRequired,
    GET_GAME: PropTypes.func.isRequired,
    GET_LATEST_REVISION: PropTypes.func.isRequired,
    GET_PROJECT_LOCALES: PropTypes.func.isRequired,
    GET_PROJECT_LOCALE_CSV: PropTypes.func.isRequired,
    GET_PROJECTS: PropTypes.func.isRequired,
    PATCH_PROJECT: PropTypes.func.isRequired,
    PATCH_PROJECT_LOCALE: PropTypes.func.isRequired,
    POST_PROJECT_LOCALE: PropTypes.func.isRequired,
  };

  static defaultProps = {};

  state = {
    locales: [],
    project: {},
    showAssetUI: false,
    showProjectDetails: false,
  };

  componentDidMount() {
    const {match, GET_GAME, GET_LATEST_REVISION, GET_PROJECTS} = this.props;
    const {gameId} = match.params;

    GET_GAME(gameId)
      .then(() => GET_LATEST_REVISION(gameId))
      .then(() => GET_PROJECTS(gameId))
      .then(this.getProjectDetails)
      .then(this.getLocales);

    document.title = 'Dized Portal: Project Information';
  }

  /**
   * Extends React Component lifecycle API with a promisified version of setState()
   *
   * @param {object} newState - new component state passed to this.setState()
   * @returns {Promise}       resolves when component state has been updated
   */
  setStatePromise(newState) {
    return new Promise((resolve) => this.setState(newState, resolve));
  }

  getLocales = () => {
    const {match, GET_PROJECT_LOCALES} = this.props;
    const {projectId} = match.params;

    return Promise.resolve()
      .then(() => GET_PROJECT_LOCALES(projectId))
      .then((response) => this.setStatePromise({locales: response.value.data}));
  };

  getProjectDetails = () => {
    const {
      match,
      projects: {projects},
    } = this.props;
    const {projectId} = match.params;
    const project = projects.find(({uuid}) => uuid === projectId);

    return this.setStatePromise({
      project: {...project},
    });
  };

  onAddLocale = (locale) => {
    const {match, POST_PROJECT_LOCALE} = this.props;
    const {projectId} = match.params;

    return Promise.resolve()
      .then(() => POST_PROJECT_LOCALE(projectId, locale))
      .then(this.getLocales);
  };

  onDeleteLocale = (locale) => {
    const {match, DELETE_PROJECT_LOCALE} = this.props;
    const {projectId} = match.params;

    return Promise.resolve()
      .then(() => DELETE_PROJECT_LOCALE(projectId, locale))
      .then(this.getLocales);
  };

  onUpdateLocale = (locale, data) => {
    const {match, PATCH_PROJECT_LOCALE} = this.props;
    const {projectId} = match.params;

    return Promise.resolve()
      .then(() => PATCH_PROJECT_LOCALE(projectId, locale, data))
      .then(this.getLocales);
  };

  onDetailsClose = () => {
    this.setProjectDetails(false);
  };

  onDetailsConfirm = (params) => {
    const {match, PATCH_PROJECT} = this.props;
    const {
      project: {description: descriptionTR1, title: titleTR1},
    } = this.state;
    const {gameId} = match.params;
    const {description: descriptionText, title: titleText} = params;

    return Promise.resolve()
      .then(
        () => new Promise((resolve) => this.setProjectDetails(false, resolve)),
      )
      .then(() =>
        PATCH_PROJECT({
          ...params,
          description: {
            ...descriptionTR1,
            text: descriptionText,
          },
          title: {
            ...titleTR1,
            text: titleText,
          },
          game: gameId,
          updated: moment().format(),
        }),
      )
      .then(this.getProjectDetails);
  };

  onEditProjectClick() {
    const {match, organization} = this.props;
    const {
      project: {type},
    } = this.state;
    const {gameId, projectId} = match.params;

    return editProject(organization, gameId, projectId, type);
  }

  onAnalyticsClick = () => {
    const {history, match, organization} = this.props;
    const {gameId, projectId} = match.params;
    history.push(`/${organization.slug}/analytics?gameId=${gameId}&projectId=${projectId}&datePreset=28days`);
  };

  onBackToGameClick = () => {
    const {history, match, organization} = this.props;
    const {gameId} = match.params;
    history.push(`/${organization.slug}/game/${gameId}`);
  };

  onAllGamesClick = () => {
    const {history, organization} = this.props;
    history.push(`/${organization.slug}/games`);
  };

  onGameClick = () => {
    this.onBackToGameClick();
  };

  onGameEditClick = () => {
    const {history, match, organization} = this.props;
    const {gameId} = match.params;
    history.push(`/${organization.slug}/game/${gameId}`);
  };

  onEditTranslations = (locale) => {
    const {match} = this.props;
    const {projectId} = match.params;

    editProjectTranslations(projectId, locale);
  };

  onProjectImageSelect = (asset) => {
    const {match, PATCH_PROJECT} = this.props;
    const {
      project: {type, uuid},
    } = this.state;
    const {gameId} = match.params;

    return Promise.resolve()
      .then(() =>
        PATCH_PROJECT({
          projectImage: assetToAO1(asset, projectImageContext),
          game: gameId,
          type,
          updated: moment().format(),
          uuid,
        }),
      )
      .then(this.getProjectDetails);
  };

  onReviewTranslations = (locale) => {
    const {match, organization} = this.props;
    const {gameId} = match.params;

    reviewTutorial(organization, gameId, locale);
  };

  onUpdateProjectClick() {
    this.setProjectDetails(true);
  }

  setProjectDetails(showProjectDetails, callback) {
    this.setState({showProjectDetails}, callback);
  }

  render() {
    const {
      match,
      organization: {uuid: organizationId},
      games = {},
      GET_PROJECT_LOCALE_CSV,
    } = this.props;
    const {locales, project, showAssetUI, showProjectDetails} = this.state;
    const {gameId, projectId} = match.params;
    const {
      description: {text: description} = {},
      projectImage,
      status,
      title: {text: title} = {},
      type,
    } = project;
    const {assetId, url = DefaultImage} = projectImage || {};
    
    // Find the current game using gameLookup for efficiency
    const currentGame = games.gameLookup?.[gameId] || {};
    const gameName = getStringFromTO(currentGame.title) || 'Game';

    return (
      <>
        <GameHeader
          gameId={gameId}
          gameName={gameName}
          projectName={title}
          onBackClick={this.onBackToGameClick}
          onGameClick={this.onGameClick}
          onAllGamesClick={this.onAllGamesClick}
        >
          <Button onClick={this.onGameEditClick}>Edit Game Details</Button>
          <Button onClick={this.onAnalyticsClick}>View Analytics</Button>
        </GameHeader>
        
        <ContentContainer>
          <Row>
            <Image
              alt="Project Art"
              onClick={() => this.setState({showAssetUI: true})}
              src={url}
            />
            <Title>
              <h3>{`Project: ${title} - ${ProjectTypeNames[type]} - ${status}`}</h3>
            </Title>

            <ProjectButtonContainer>
              <ButtonRow>
                <Button style={{ width: '260px'}} onClick={() => this.onUpdateProjectClick()}>
                  Edit Project Details
                </Button>
 
              </ButtonRow>
              <ButtonRow>
                <Button style={{ width: '260px', background: '#5394c6'}} onClick={() => this.onEditProjectClick()}>
                  Edit Project
                </Button>
              </ButtonRow>
            </ProjectButtonContainer>
          </Row>
          {description && (
            <Row>
              <Title>
                <h4>{description}</h4>
              </Title>
            </Row>
          )}
          <Row>
            <Title>
              <h2>Project Languages</h2>
              <p style={{ color: '#666', fontSize: '14px', margin: '8px 0 16px 0' }}>
                Manage translations for this project. Add new languages, edit existing translations, or download CSV files for offline work. The master locale contains the original content that other translations are based on.
              </p>
            </Title>
          </Row>
          
          <TranslationDownload
            title="Download Translation Files"
            description="Download CSV files containing all translations for offline editing. Files include the master locale content."
            generateFile={
              title ? (filename) =>
                Promise.resolve()
                  .then(() => {
                    // generate CSV for master locale
                    const {locale} = locales.find(({master}) => master) ?? {};
                    if (!locale)
                      throw new Error(
                        `no master locale found in project '${projectId}'`,
                      );

                    return GET_PROJECT_LOCALE_CSV(projectId, locale);
                  })
                  .then(
                    ({value: {data}}) =>
                      new File([data], filename, {type: 'text/csv'}),
                  ) : undefined
            }
            filename={title ? `Translations ${ProjectTypeNames[type]} ${title}.csv` : ''}
            buttonText="Download CSV"
            disabled={!title}
          />
          
          <LocaleTable
            locales={locales}
            localeActions={(locale, localeData) => (
              <>
                {type === ProjectTypes.TUTORIAL_CT && (
                  <GridButton onClick={() => this.onReviewTranslations(locale)}>
                    Review
                  </GridButton>
                )}
                <GridButton onClick={() => this.onEditTranslations(locale)}>
                  Edit
                </GridButton>
              </>
            )}
            localeMasterActions={(locale) => (
              <>
                {type === ProjectTypes.TUTORIAL_CT && (
                  <GridButton onClick={() => this.onReviewTranslations(locale)}>
                    Review
                  </GridButton>
                )}
                <GridButton onClick={() => this.onEditProjectClick()}>
                  Edit
                </GridButton>
              </>
            )}
            onAddLocale={this.onAddLocale}
            onDeleteLocale={this.onDeleteLocale}
            onUpdateLocale={this.onUpdateLocale}
          />
          {showProjectDetails && (
            <ProjectDetails
              {...project}
              onClose={this.onDetailsClose}
              onConfirm={this.onDetailsConfirm}
            />
          )}
        </ContentContainer>

        <AssetSelector
          assetId={assetId}
          locale={assetAttr.locale}
          organizationId={organizationId}
          onClose={() => this.setState({showAssetUI: false})}
          onSelectAsset={(asset) => {
            console.log(asset);
            if (this.onProjectImageSelect) this.onProjectImageSelect(asset);
            this.setState({showAssetUI: false});
          }}
          visible={showAssetUI}
        />
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectInfo);
